import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Currency } from "@front/m19-api-client";
import { AuthService } from "@front/m19-services";
import { IAlertComponent } from "@front/m19-ui";
import { API_DATE_FORMAT } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DateTime } from "luxon";
import { map } from "rxjs";

@Component({
  selector: "app-monthly-budget-warning",
  imports: [CommonModule, IAlertComponent, TranslocoDirective],
  template: ` <ng-container *transloco="let t">
    @if (remainingDaysInMonth() !== undefined) {
      <IAlert type="warning">
        @if (budgetAlreadyExceeded()) {
          {{
            t("algo-mode-selection.budget_already_exceeded", [
              spend() | currency: currencyCode() : "symbol" : "1.2-2" : locale(),
              value() | currency: currencyCode() : "symbol" : "1.2-2" : locale(),
            ])
          }}
        } @else {
          {{
            t("algo-mode-selection.budget_for_month", [
              value() | currency: currencyCode() : "symbol" : "1.2-2" : locale(),
              endOfCurrentMonthDate() | date: "MMMM y" : undefined : locale(),
            ])
          }}, {{ t("algo-mode-selection.budget_for_month_remaining", { count: remainingDaysInMonth() }) }}
        }
        <div>
          {{
            t("algo-mode-selection.next_month_budget_for_month", [nextMonth() | date: "MMMM y" : undefined : locale()])
          }}
        </div>
      </IAlert>
    }
  </ng-container>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthlyBudgetWarningComponent {
  readonly translocoService = inject(TranslocoService);
  readonly authService = inject(AuthService);

  readonly value = input.required<number>();
  readonly currencyCode = input.required<Currency>();
  readonly spend = input<number>();

  readonly locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)));

  private endOfCurrentMonth = signal(DateTime.fromISO(DateTime.now().toFormat(API_DATE_FORMAT)).endOf("month"));
  endOfCurrentMonthDate = computed(() => this.endOfCurrentMonth().toJSDate());
  remainingDaysInMonth = computed(() => Math.floor(this.endOfCurrentMonth().diff(DateTime.now(), "day").days));
  nextMonth = computed(() => this.endOfCurrentMonth().plus({ month: 1 }).toJSDate());
  budgetAlreadyExceeded = computed(() => {
    const spend = this.spend();
    return spend !== undefined && spend >= this.value();
  });
}
